/**
 * The zoom figure — how Human Design slices one circle six times over:
 * wheel → 64 gates → 6 lines → 6 colours → 6 tones → 5 bases, with how long
 * the followed planet takes to cross one cell of each.
 *
 * Mounted by the knowledge base ({{zoom}}) and the app's Advanced tab, so it
 * reads nothing from the page: `mount(el, opts)` is the whole contract.
 * The degree constants and gate order are copied from the engine;
 * backend_api/tests/test_zoom_widget.py fails if they drift.
 */
(function () {
  "use strict";

  window.GHD = window.GHD || {};

  // ── The engine's numbers (backend/engine/constants.py) ────────────────────
  const GATE_START_LONGITUDE = 302.0;
  const DEGREES_PER_GATE = 360.0 / 64;
  const DEGREES_PER_LINE = DEGREES_PER_GATE / 6;
  const DEGREES_PER_COLOR = DEGREES_PER_LINE / 6;
  const DEGREES_PER_TONE = DEGREES_PER_COLOR / 6;
  const DEGREES_PER_BASE = DEGREES_PER_TONE / 5;

  // ICHING_CIRCLE — the 64 gates in wheel order, gate 41 first at 302°.
  const ICHING_CIRCLE = [
    41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35, 45, 12, 15, 52, 39, 53,
    62, 56, 31, 33, 7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50, 28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58,
    38, 54, 61, 60,
  ];

  // ── The six bars ─────────────────────────────────────────────────────────
  const LAYERS = [
    { id: "wheel", name: "Wheel", cells: 1, width: 360, numbered: false, sub: "360°, starting at gate 41 (302°)" },
    { id: "gate", name: "Gate", cells: 64, width: DEGREES_PER_GATE, numbered: false, sub: "64 gates of 5.625°" },
    { id: "line", name: "Line", cells: 6, width: DEGREES_PER_LINE, numbered: true, sub: "6 lines of 0.9375°" },
    { id: "color", name: "Color", cells: 6, width: DEGREES_PER_COLOR, numbered: true, sub: "6 colors of 0.15625°" },
    { id: "tone", name: "Tone", cells: 6, width: DEGREES_PER_TONE, numbered: true, sub: "6 tones of 0.026°" },
    { id: "base", name: "Base", cells: 5, width: DEGREES_PER_BASE, numbered: true, sub: "5 bases of 0.0052°" },
  ];

  // Mean daily motion, degrees per day. Mean, and the legend says so: the Sun
  const SIDEREAL_YEAR_DAYS = 365.256363;
  const SIDEREAL_MONTH_DAYS = 27.321582;
  const NODE_DEG_PER_DAY = -0.05295392;
  const PLANETS = [
    { id: "Sun", key: "chart.planet.sun", fallback: "Sun", degPerDay: 360 / SIDEREAL_YEAR_DAYS },
    { id: "Earth", key: "chart.planet.earth", fallback: "Earth", degPerDay: 360 / SIDEREAL_YEAR_DAYS },
    { id: "North Node", key: "chart.planet.northnode_short", fallback: "N.Node", degPerDay: NODE_DEG_PER_DAY },
    { id: "South Node", key: "chart.planet.southnode_short", fallback: "S.Node", degPerDay: NODE_DEG_PER_DAY },
    { id: "Moon", key: "chart.planet.moon", fallback: "Moon", degPerDay: 360 / SIDEREAL_MONTH_DAYS },
  ];

  // A payload may spell the nodes either way ("North Node" from the engine,
  // "NorthNode" from the mandala widget), and case is not worth an argument.
  function planetKey(name) {
    return String(name || "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");
  }
  const PLANET_BY_KEY = {};
  PLANETS.forEach((p) => {
    PLANET_BY_KEY[planetKey(p.id)] = p;
  });

  // ── The worked example ───────────────────────────────────────────────────
  //
  // A real moment where the Sun sits mid-cell at every layer. The longitudes are
  // the engine's; the backend test recomputes them from this instant.
  const EXAMPLE_MOMENT = "2024-03-21T14:32:00Z";
  const EXAMPLE = [
    { planet: "Sun", side: "personality", longitude: 1.465813 },
    { planet: "Earth", side: "personality", longitude: 181.465813 },
    { planet: "North Node", side: "personality", longitude: 15.670463 },
    { planet: "South Node", side: "personality", longitude: 195.670463 },
    { planet: "Moon", side: "personality", longitude: 141.511305 },
  ];

  const SLIDER_MINUTES = 60;

  // ── Pure helpers ─────────────────────────────────────────────────────────

  /** Longitude → the cell it occupies in every one of the six bars. */
  function locate(longitude) {
    const offset = ((((longitude - GATE_START_LONGITUDE) % 360) + 360) % 360) % 360;
    const gateIndex = Math.min(Math.floor(offset / DEGREES_PER_GATE), 63);
    let rest = offset - gateIndex * DEGREES_PER_GATE;

    const line = Math.min(Math.floor(rest / DEGREES_PER_LINE) + 1, 6);
    rest -= (line - 1) * DEGREES_PER_LINE;
    const color = Math.min(Math.floor(rest / DEGREES_PER_COLOR) + 1, 6);
    rest -= (color - 1) * DEGREES_PER_COLOR;
    const tone = Math.min(Math.floor(rest / DEGREES_PER_TONE) + 1, 6);
    rest -= (tone - 1) * DEGREES_PER_TONE;
    const base = Math.min(Math.floor(rest / DEGREES_PER_BASE) + 1, 5);

    return {
      longitude: ((longitude % 360) + 360) % 360,
      offset: offset,
      gate: ICHING_CIRCLE[gateIndex],
      gateIndex: gateIndex,
      line: line,
      color: color,
      tone: tone,
      base: base,
      // Zero-based cell index per bar, which is what the drawing wants.
      index: { wheel: 0, gate: gateIndex, line: line - 1, color: color - 1, tone: tone - 1, base: base - 1 },
    };
  }

  /** Whether an activation places its body finer than its line. A free chart
   *  carries neither the longitude nor the colour, and its position is then
   *  only the middle of the line. */
  function isPrecise(act) {
    return (typeof act.longitude === "number" && isFinite(act.longitude)) || act.color != null;
  }

  /** The cell centre of an activation given only its gate/line/colour/tone/base. */
  function longitudeOf(act) {
    if (typeof act.longitude === "number" && isFinite(act.longitude)) return act.longitude;
    const gateIndex = ICHING_CIRCLE.indexOf(Number(act.gate));
    if (gateIndex < 0) return null;
    if (act.color == null) {
      const lineMiddle = gateIndex * DEGREES_PER_GATE + ((Number(act.line) || 1) - 0.5) * DEGREES_PER_LINE;
      return (GATE_START_LONGITUDE + lineMiddle) % 360;
    }
    const offset =
      gateIndex * DEGREES_PER_GATE +
      ((Number(act.line) || 1) - 1) * DEGREES_PER_LINE +
      ((Number(act.color) || 1) - 1) * DEGREES_PER_COLOR +
      ((Number(act.tone) || 1) - 1) * DEGREES_PER_TONE +
      ((Number(act.base) || 1) - 1) * DEGREES_PER_BASE +
      DEGREES_PER_BASE / 2;
    return (GATE_START_LONGITUDE + offset) % 360;
  }

  function interpolate(text, vars) {
    if (!vars) return text;
    return String(text).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : String(vars[k])));
  }

  /** The page's language, which decides the decimal mark: the docs stamp it on */
  function pageLang() {
    const lang = typeof document !== "undefined" && document.documentElement && document.documentElement.lang;
    return lang || "en";
  }

  function trimZero(n, digits) {
    try {
      return new Intl.NumberFormat(pageLang(), { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(n);
    } catch (e) {
      return n.toFixed(digits).replace(/\.0+$/, "");
    }
  }

  /** How long this planet takes to cross `degrees`, in the largest unit that
   * still reads as a number a person can hold: years for an orbit, seconds for
   * the Moon crossing one base. */
  function crossing(degrees, degPerDay, tr) {
    const days = degrees / Math.abs(degPerDay);
    if (days >= 300)
      return tr("fig.zoom.duration", "{n} {unit}", {
        n: trimZero(days / 365.2422, 1),
        unit: tr("fig.zoom.unit_year", "yr"),
      });
    if (days >= 2)
      return tr("fig.zoom.duration", "{n} {unit}", { n: trimZero(days, 1), unit: tr("fig.zoom.unit_day", "d") });
    const hours = days * 24;
    if (hours >= 2)
      return tr("fig.zoom.duration", "{n} {unit}", { n: trimZero(hours, 1), unit: tr("fig.zoom.unit_hour", "h") });
    const minutes = hours * 60;
    if (minutes >= 1.5) {
      const n = minutes >= 10 ? String(Math.round(minutes)) : trimZero(minutes, 1);
      return tr("fig.zoom.duration", "{n} {unit}", { n: n, unit: tr("fig.zoom.unit_minute", "min") });
    }
    return tr("fig.zoom.duration", "{n} {unit}", {
      n: String(Math.round(minutes * 60)),
      unit: tr("fig.zoom.unit_second", "s"),
    });
  }

  function el(tag, cls, text) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  const SVG_NS = "http://www.w3.org/2000/svg";

  /** A Feather-style stroke icon: 24×24, currentColor, width 2, round caps. */
  function icon(cls, d, rect) {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", cls);
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    if (rect) {
      const box = document.createElementNS(SVG_NS, "rect");
      Object.keys(rect).forEach((k) => box.setAttribute(k, rect[k]));
      svg.appendChild(box);
    }
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);
    return svg;
  }

  function lockIcon() {
    return icon("zm-lock", "M7 11V7a5 5 0 0 1 10 0v4", {
      x: "3",
      y: "11",
      width: "18",
      height: "11",
      rx: "2",
      ry: "2",
    });
  }

  // ── The widget ───────────────────────────────────────────────────────────

  /**
   * Mount the figure into `host`.
   *
   * opts.activations  [{planet, side, longitude?, gate?, line?, color?, tone?,
   *                    base?}]; empty means the worked example.
   * opts.locked       layer ids drawn locked (padlock, no numbers).
   * opts.t            (key, fallback) => string.
   * opts.links        {layerId: href} for the row names.
   * opts.rowLink      (layerId) => an anchor the CALLER owns; the widget only
   *                    brands it. Takes precedence over opts.links.
   * opts.lockHref, opts.lockLabel  make the padlock a labelled link.
   * opts.dialogParent where the expanded <dialog> is appended (default body).
   *                    A host that delegates clicks passes its delegation
   *                    root: events bubble along the tree, not the top layer.
   */
  function mount(host, opts) {
    if (!host) return null;
    const options = opts || {};
    const baseT = typeof options.t === "function" ? options.t : (key, fallback) => fallback;
    const tr = (key, fallback, vars) => interpolate(baseT(key, fallback), vars);
    const links = options.links || {};
    const rowLink = typeof options.rowLink === "function" ? options.rowLink : null;
    const locked = {};
    (options.locked || []).forEach((id) => {
      locked[id] = true;
    });

    const supplied = (options.activations || []).filter((a) => longitudeOf(a) != null);
    const isExample = supplied.length === 0;
    const activations = isExample ? EXAMPLE.slice() : supplied;

    // Which bodies this payload actually offers, in our canonical order.
    const available = PLANETS.filter((p) => activations.some((a) => planetKey(a.planet) === planetKey(p.id)));
    if (!available.length) return null;

    const sides = [];
    ["personality", "design"].forEach((side) => {
      if (activations.some((a) => (a.side || "personality") === side)) sides.push(side);
    });

    const state = {
      planet: available[0].id,
      side: sides[0] || "personality",
      minutes: 0,
      expanded: false,
    };

    host.textContent = "";
    host.classList.add("zm");

    // ── Controls ───────────────────────────────────────────────────────────
    const controls = el("div", "zm-controls");

    const chips = el("div", "zm-chips");
    chips.setAttribute("role", "group");
    chips.setAttribute("aria-label", tr("fig.zoom.planets_aria", "Which body to follow"));
    const chipButtons = available.map((p) => {
      const btn = el("button", "zm-chip", tr(p.key, p.fallback));
      btn.type = "button";
      btn.dataset.planet = p.id;
      chips.appendChild(btn);
      return btn;
    });
    controls.appendChild(chips);

    let sideButtons = [];
    if (sides.length > 1) {
      const sideGroup = el("div", "zm-sides");
      sideGroup.setAttribute("role", "group");
      sideGroup.setAttribute("aria-label", tr("fig.zoom.sides_aria", "Which side of the chart"));
      sideButtons = sides.map((side) => {
        const label =
          side === "design" ? tr("fig.zoom.side_design", "Design") : tr("fig.zoom.side_personality", "Personality");
        const btn = el("button", "zm-side", label);
        btn.type = "button";
        btn.dataset.side = side;
        sideGroup.appendChild(btn);
        return btn;
      });
      controls.appendChild(sideGroup);
    }

    const expandBtn = el("button", "zm-expand");
    expandBtn.type = "button";
    expandBtn.appendChild(icon("zm-expand-icon", "M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"));
    expandBtn.appendChild(el("span", null, tr("fig.zoom.expand", "Expand")));
    controls.appendChild(expandBtn);
    host.appendChild(controls);

    // ── Slider ─────────────────────────────────────────────────────────────
    const sliderWrap = el("div", "zm-slider");
    const sliderId = "zm-slider-" + Math.random().toString(36).slice(2, 9);
    const sliderLabel = el("label", "zm-slider-label", tr("fig.zoom.slider_label", "Birth time ± 60 minutes"));
    sliderLabel.setAttribute("for", sliderId);
    const slider = document.createElement("input");
    slider.type = "range";
    slider.id = sliderId;
    slider.className = "zm-range";
    slider.min = String(-SLIDER_MINUTES);
    slider.max = String(SLIDER_MINUTES);
    slider.step = "1";
    slider.value = "0";
    const sliderValue = el("output", "zm-slider-value");
    sliderValue.setAttribute("for", sliderId);
    sliderWrap.appendChild(sliderLabel);
    sliderWrap.appendChild(slider);
    sliderWrap.appendChild(sliderValue);
    host.appendChild(sliderWrap);

    // ── The six bars ───────────────────────────────────────────────────────
    const stack = el("div", "zm-stack");
    const head = el("div", "zm-head");
    head.appendChild(el("span", "zm-head-layer", tr("fig.zoom.head_layer", "Layer")));
    head.appendChild(el("span", null, ""));
    head.appendChild(el("span", "zm-head-cross", tr("fig.zoom.head_crossing", "One cell takes")));
    stack.appendChild(head);

    const rows = LAYERS.map((layer, i) => {
      // Between two bars, a wedge from the accented cell above to the full
      let funnel = null;
      if (i > 0) {
        const node = el("div", "zm-funnel");
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("viewBox", "0 0 100 10");
        svg.setAttribute("preserveAspectRatio", "none");
        svg.setAttribute("aria-hidden", "true");
        const wedge = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        wedge.setAttribute("class", "zm-funnel-fill");
        const edges = document.createElementNS("http://www.w3.org/2000/svg", "path");
        edges.setAttribute("class", "zm-funnel-edge");
        edges.setAttribute("vector-effect", "non-scaling-stroke");
        svg.appendChild(wedge);
        svg.appendChild(edges);
        node.appendChild(svg);
        stack.appendChild(node);
        funnel = { node: node, wedge: wedge, edges: edges, above: LAYERS[i - 1] };
      }

      const row = el("div", "zm-row");
      row.dataset.layer = layer.id;

      const rowHead = el("div", "zm-row-head");
      const nameText = tr("fig.zoom.row_" + layer.id, layer.name);
      // The caller's own anchor first: a host that routes its links itself (the
      // app's KB panel) hands one over. classList, never className: the
      // caller's classes are what its delegated click looks for.
      let name = rowLink ? rowLink(layer.id) : null;
      if (name) {
        name.classList.add("zm-row-name");
        name.textContent = nameText;
      } else if (links[layer.id]) {
        name = el("a", "zm-row-name", nameText);
        name.href = links[layer.id];
      } else {
        name = el("span", "zm-row-name", nameText);
      }
      const value = el("span", "zm-row-val");
      const sub = el("span", "zm-row-sub");
      const nameLine = el("span", "zm-row-line");
      nameLine.appendChild(name);
      nameLine.appendChild(value);
      rowHead.appendChild(nameLine);
      rowHead.appendChild(sub);

      const barWrap = el("div", "zm-bar-wrap");
      const bar = el("div", "zm-bar");
      const cells = [];
      for (let c = 0; c < layer.cells; c += 1) {
        const cell = el("div", "zm-cell");
        if (layer.numbered) cell.appendChild(el("span", "zm-cell-n", String(c + 1)));
        bar.appendChild(cell);
        cells.push(cell);
      }
      // The wheel is one cell, so "the accented cell" would be the whole bar and
      let mark = null;
      if (layer.id === "wheel") {
        mark = el("span", "zm-mark");
        cells[0].appendChild(mark);
      }
      barWrap.appendChild(bar);

      const cross = el("div", "zm-cross");
      const crossValue = el("span", "zm-cross-v");
      cross.appendChild(crossValue);

      row.appendChild(rowHead);
      row.appendChild(barWrap);
      row.appendChild(cross);
      stack.appendChild(row);

      return {
        layer: layer,
        funnel: funnel,
        row: row,
        value: value,
        sub: sub,
        cells: cells,
        mark: mark,
        cross: crossValue,
      };
    });
    host.appendChild(stack);

    const status = el("p", "zm-status");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    host.appendChild(status);

    // ── Legend ─────────────────────────────────────────────────────────────
    //
    // Folded by default, as a native <details>. Styled in zoom.css itself (the
    // docs load nothing else), and not height-animated: that driver lives only
    // on the app's route.
    const legend = el("details", "zm-legend");
    const legendHead = el("summary", "zm-legend-head");
    const legendCaret = el("span", "zm-legend-caret", "›");
    legendCaret.setAttribute("aria-hidden", "true");
    legendHead.appendChild(legendCaret);
    legendHead.appendChild(el("span", null, tr("fig.zoom.legend_head", "How to read this")));
    legend.appendChild(legendHead);
    const legendList = el("ul", "zm-legend-list");
    const legendItems = [
      ["fig.zoom.legend_rows", "Every bar is the colored cell of the bar above it, opened back out to full width."],
      ["fig.zoom.legend_accent", "The colored cell is where the body you are following sits."],
      [
        "fig.zoom.legend_column",
        "The right-hand column is how long that body takes to cross one cell of that bar. The smaller the slice, the more exact your birth time has to be.",
      ],
      [
        "fig.zoom.legend_slider",
        "Move the slider and watch from the bottom up: the base changes first, then the tone, then the color. The line almost never moves.",
      ],
    ];
    if (Object.keys(locked).length) {
      legendItems.push(["fig.zoom.legend_locked", "A bar with a padlock is a layer your plan does not show."]);
    }
    legendItems.push([
      "fig.zoom.legend_approx",
      "Crossing times use each body's average speed. The Sun runs faster in January than in July, so this shows the pattern, not the exact minute a layer flips.",
    ]);
    legendItems.forEach((pair) => legendList.appendChild(el("li", null, tr(pair[0], pair[1]))));
    legend.appendChild(legendList);
    host.appendChild(legend);
    // Outside the fold, deliberately: "not your chart" is the one line here a
    // reader must not have to open anything to see.
    if (isExample) {
      host.appendChild(
        el(
          "p",
          "zm-legend-example",
          tr("fig.zoom.example_note", "A worked example, not your chart: the sky at {moment}.", {
            moment: EXAMPLE_MOMENT.replace("T", " ").replace(":00Z", " UTC"),
          }),
        ),
      );
    }

    // The expanded view lives in a <dialog>: showModal() carries Esc, the
    let dialog = null;
    let placeholder = null;

    // ── Drawing ────────────────────────────────────────────────────────────
    function render() {
      const planet = PLANET_BY_KEY[planetKey(state.planet)] || PLANETS[0];
      const act =
        activations.find(
          (a) => planetKey(a.planet) === planetKey(state.planet) && (a.side || "personality") === state.side,
        ) || activations.find((a) => planetKey(a.planet) === planetKey(state.planet));
      const born = act ? longitudeOf(act) : null;
      const precise = !act || isPrecise(act);
      const drift = planet.degPerDay * (state.minutes / 1440);
      const here = locate((born == null ? 0 : born) + drift);

      chipButtons.forEach((btn) => {
        const on = planetKey(btn.dataset.planet) === planetKey(state.planet);
        btn.classList.toggle("is-on", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      });
      sideButtons.forEach((btn) => {
        const on = btn.dataset.side === state.side;
        btn.classList.toggle("is-on", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      });

      const sign = state.minutes > 0 ? "+" : state.minutes < 0 ? "−" : "";
      sliderValue.textContent = tr("fig.zoom.slider_value", "{sign}{n} min", {
        sign: sign,
        n: Math.abs(state.minutes),
      });

      expandBtn.querySelector("span").textContent = state.expanded
        ? tr("fig.zoom.close", "Close")
        : tr("fig.zoom.expand", "Expand");

      rows.forEach((r) => {
        const layer = r.layer;
        const isLocked = !!locked[layer.id];
        r.row.classList.toggle("is-locked", isLocked);

        r.sub.textContent = tr("fig.zoom.sub_" + layer.id, layer.sub);

        if (isLocked) {
          // The padlock, and where it leads. A chip that is a link needs a name
          r.value.textContent = "";
          const chip = el(options.lockHref ? "a" : "span", "zm-lock-chip");
          if (options.lockHref) {
            chip.href = options.lockHref;
            chip.setAttribute(
              "aria-label",
              tr("fig.zoom.row_" + layer.id, layer.name) +
                " · " +
                (options.lockLabel || tr("fig.zoom.locked_aria", "Locked")),
            );
          }
          chip.appendChild(lockIcon());
          if (options.lockLabel) chip.appendChild(el("span", null, options.lockLabel));
          if (!options.lockHref) chip.appendChild(el("span", "zm-sr", tr("fig.zoom.locked_aria", "Locked")));
          r.value.appendChild(chip);
        } else {
          r.value.textContent =
            layer.id === "wheel"
              ? tr("fig.zoom.degrees", "{deg}°", {
                  deg: precise ? trimZero(here.longitude, 2) : "≈" + trimZero(here.longitude, 1),
                })
              : String(here[layer.id]);
        }

        const at = here.index[layer.id];
        r.cells.forEach((cell, i) => {
          cell.classList.toggle("is-on", !isLocked && !r.mark && i === at);
        });
        if (r.mark) {
          r.mark.hidden = isLocked;
          r.mark.style.left = ((here.offset / 360) * 100).toFixed(3) + "%";
        }

        r.cross.textContent = crossing(layer.width, planet.degPerDay, tr);

        if (r.funnel) {
          const aboveLayer = r.funnel.above;
          const aboveLocked = !!locked[aboveLayer.id];
          r.funnel.node.className = "zm-funnel" + (isLocked || aboveLocked ? " is-locked" : "");
          // A locked bar above shows no cell, so the wedge opens from the whole
          // width rather than pointing at a position the reader cannot see.
          const idx = here.index[aboveLayer.id];
          const w = 100 / aboveLayer.cells;
          const x1 = aboveLocked ? 0 : idx * w;
          const x2 = aboveLocked ? 100 : (idx + 1) * w;
          r.funnel.wedge.setAttribute("points", x1.toFixed(3) + ",0 " + x2.toFixed(3) + ",0 100,10 0,10");
          r.funnel.edges.setAttribute("d", "M " + x1.toFixed(3) + " 0 L 0 10 M " + x2.toFixed(3) + " 0 L 100 10");
        }
      });

      const readable = [];
      LAYERS.forEach((layer) => {
        if (layer.id === "wheel") return;
        if (locked[layer.id]) return;
        readable.push(tr("fig.zoom.row_" + layer.id, layer.name) + " " + String(here[layer.id]));
      });
      status.textContent = tr("fig.zoom.status", "{planet}: {parts}", {
        planet: tr(planet.key, planet.fallback),
        parts: readable.join(" · "),
      });
    }

    // ── Wiring ─────────────────────────────────────────────────────────────
    // Listeners last, so nothing names render() before it exists.
    function collapse() {
      if (!dialog) return;
      if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.replaceChild(host, placeholder);
        placeholder = null;
      }
      host.classList.remove("zm-in-dialog");
      state.expanded = false;
      dialog.remove();
      dialog = null;
      render();
    }

    // A link tapped inside the expanded figure takes the reader somewhere, and a
    // modal left open would trap focus there, so close it. `close()` fires its
    // event in a later task, so the click still bubbles to the host's handler.
    host.addEventListener("click", (e) => {
      if (!dialog) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target && e.target.closest && e.target.closest("a[href]");
      if (!link || !host.contains(link)) return;
      // A new tab leaves this one as it was, figure open and all.
      if (link.target && link.target !== "_self") return;
      dialog.close();
    });

    chipButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        state.planet = btn.dataset.planet;
        render();
      });
    });
    sideButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        state.side = btn.dataset.side;
        render();
      });
    });
    slider.addEventListener("input", () => {
      state.minutes = Number(slider.value);
      render();
    });
    expandBtn.addEventListener("click", () => {
      if (dialog) {
        dialog.close();
        return;
      }
      if (!host.parentNode) return;
      dialog = document.createElement("dialog");
      dialog.className = "zm-dialog";
      const closeBtn = el("button", "zm-dialog-close");
      closeBtn.type = "button";
      closeBtn.setAttribute("aria-label", tr("fig.zoom.close", "Close"));
      closeBtn.textContent = "×";
      closeBtn.addEventListener("click", () => dialog.close());
      dialog.appendChild(closeBtn);
      placeholder = el("div", "zm-placeholder");
      host.parentNode.replaceChild(placeholder, host);
      host.classList.add("zm-in-dialog");
      dialog.appendChild(host);
      (options.dialogParent || document.body).appendChild(dialog);
      dialog.addEventListener("close", collapse);
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) dialog.close();
      });
      if (dialog.showModal) {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      state.expanded = true;
      render();
    });

    render();
    return { render: render, state: state, close: collapse };
  }

  GHD.zoom = {
    mount: mount,
    locate: locate,
    longitudeOf: longitudeOf,
    LAYERS: LAYERS,
    PLANETS: PLANETS,
    EXAMPLE: EXAMPLE,
    EXAMPLE_MOMENT: EXAMPLE_MOMENT,
    GATE_START_LONGITUDE: GATE_START_LONGITUDE,
    DEGREES_PER_GATE: DEGREES_PER_GATE,
    DEGREES_PER_LINE: DEGREES_PER_LINE,
    DEGREES_PER_COLOR: DEGREES_PER_COLOR,
    DEGREES_PER_TONE: DEGREES_PER_TONE,
    DEGREES_PER_BASE: DEGREES_PER_BASE,
    ICHING_CIRCLE: ICHING_CIRCLE,
    crossing: crossing,
  };

  // ── Boot, for the knowledge base ─────────────────────────────────────────
  // A doc page boots the whole document; the in-app panel (kb-panel.js) boots
  // the article it just inserted, which may arrive after this file evaluated.
  function boot(root, opts) {
    (root || document).querySelectorAll("[data-zoom]").forEach((node) => {
      if (node.dataset.zoomMounted) return;
      node.dataset.zoomMounted = "1";
      const links = {};
      node.querySelectorAll("[data-zoom-layer]").forEach((a) => {
        links[a.getAttribute("data-zoom-layer")] = a.getAttribute("href");
      });
      mount(node, {
        t: window.GHD_DOCS_T,
        links: links,
        activations: [],
        locked: [],
        dialogParent: opts && opts.dialogParent,
      });
    });
  }

  GHD.zoom.boot = boot;

  if (window.GHD_ZOOM_NO_BOOT) return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }
})();
